<template>
  <div
    ref="table"
    class="graphql-table-wrapper"
    :class="{
      isFullscreen,
      'graphql-table-wrapper--contain-bulk-bar': props.containBulkActionBar,
    }"
  >
    <GraphqlDataTableFilters
      v-model:filters="activeFilterValues"
      v-model:show="showFilters"
      :query="stringifiedQuery"
      :filter-black-list="filterBlackList"
      :persisted-key="persistedKey"
    />
    <BaseDataTable
      v-model="effectiveSelectedRows"
      :entries
      :columns
      :loading="loading"
      :paginator-info="paginatorInfo"
      :default-page-length="paginationPerPage"
      :default-page-length-options="defaultPerPageOptions"
      :prevent-row-selection-on-last-td="!!slots.action"
      :data-test="dataTest || 'graphql-data-table'"
      :preserve-selected-rows="props.preserveSelectedRows"
      multi-select-target-key="id"
      row-uuid="id"
      :multi-select="bulkActionsActive || props.multiSelect"
      :max-height="
        isFullscreen
          ? 'calc(100vh - 76px)'
          : `calc(100vh - 46px - 24px - 56px - 20px - 60px ${maxHeightAdjust})`
      "
      row-hover
      @search="handleTableSearch"
      @row-click="$emit('rowClick', $event)"
      @select-all="handleSelectAll"
      @deselect-all="handleDeselectAll"
      @row-selection-toggled="handleRowSelectionToggled"
      @column-dragover="dragEventHandler('dragover', $event)"
      @column-dragstart="dragEventHandler('dragstart', $event)"
      @column-dragend="dragEventHandler('dragend', $event)"
      @column-drag="dragEventHandler('drag', $event)"
      @column-dragenter="dragEventHandler('dragenter', $event)"
      @column-dragleave="dragEventHandler('dragleave', $event)"
    >
      <template v-if="error && !loading && !loadDataRegardlessOfError" #table-content>
        <slot
          name="error"
          :entries="entries"
          :error="error"
          :load-data-regardless-of-error="loadDataRegardlessOfError"
        >
          <WdsStack direction-row justify-center>
            <GraphqlDataTableErrorNotice
              :has-data="entries?.length > 0"
              @load-data="loadDataRegardlessOfError = true"
            />
          </WdsStack>
        </slot>
      </template>
      <template #no-results="{ loading: l }">
        <WdsStack direction-row justify-center>
          <slot v-if="l" name="loading-notice">
            <BaseLoadingNotice class="tw-z-10" data-test="tc-loading">
              <template #main>
                {{ $t('Finding matching data') }}
              </template>
            </BaseLoadingNotice>
          </slot>
          <template v-else-if="!l && !error">
            <slot name="no-results">
              {{ $t('No matching records found') }}
            </slot>
          </template>
        </WdsStack>
      </template>
      <template #control-bar>
        <slot
          name="control-bar"
          :search="search"
          :show-filters="showFilters"
          :order-by="orderBy"
          :full-screen-mode="isFullscreen"
          :number-of-items="totalRowCount"
          :num-active-filters="activeFilterValueCounts"
          :query="query"
          :search-placeholder="props.searchPlaceholder"
          :show-download-modal="showDownloadModal"
          :toggle-fullscreen="toggleFullscreen"
          :column-black-list="columnBlackList"
        >
          <GraphqlDataTableControlBar
            v-model:search="search"
            v-model:show-filters="showFilters"
            v-model:order-by="orderBy"
            :full-screen-mode="isFullscreen"
            :number-of-items="totalRowCount"
            :num-active-filters="activeFilterValueCounts"
            :query="query"
            :search-placeholder="props.searchPlaceholder"
            :sort-options="props.sortOptions"
            :allow-cross-table-columns
            :hide-filters="props.hideFilters"
            :column-black-list="columnBlackList"
            @download="showDownloadModal = true"
            @full-screen="toggleFullscreen"
            @reset-filters="resetAllFilters"
          >
            <template v-if="(quickFilters?.length ?? 0) > 0 || !!$slots.filters" #filters>
              <slot name="filters">
                <WdsStack
                  v-if="(quickFilters?.length ?? 0) > 0"
                  direction-row
                  justify-center
                  gap-sm
                >
                  <GraphqlDataTableQuickFilter
                    v-if="quickFilters?.[0]"
                    v-model="activeFilterValues[quickFilters[0]]"
                    :query="stringifiedQuery"
                    :argument-string="quickFilters?.[0]"
                  />
                  <GraphqlDataTableQuickFilter
                    v-if="quickFilters?.[1]"
                    v-model="activeFilterValues[quickFilters[1]]"
                    :query="stringifiedQuery"
                    :argument-string="quickFilters?.[1]"
                  />
                </WdsStack>
              </slot>
            </template>
          </GraphqlDataTableControlBar>
        </slot>
      </template>
      <template #action="{ row, rowIndex }">
        <slot name="action" :row="row" :index="rowIndex" :toggle-selection="toggleRowSelection"></slot>
      </template>
    </BaseDataTable>
    <div class="graphql-table-globals-wrapper" :class="globalsClass">
      <slot name="full-screen-globals" />
      <WdsStack v-if="props.bulkActions.length > 0" justify-center align-center>
        <div>
          <ActionBar
            width="auto"
            :hide="selectedRowCount === 0 && !hasPersistentBulkAction"
            :pin-to-parent="props.containBulkActionBar"
            :z-index="props.containBulkActionBar ? 40 : 9"
            class="tw-border-2 tw-border-solid tw-border-indigo-300"
          >
            <WdsStack class="tw-max-w-full tw-w-[800px]" align-center justify-space-between>
              <WdsStack align-center gap-xs>
                <WdsButtonIcon transparent-background sm @click="clearSelection">
                  <WdsIcon name="close" :size="16" />
                </WdsButtonIcon>
                <WdsText primary>
                  <span class="tw-text-indigo-500">
                    {{ $t(':count selected', { count: selectedRowCount }) }}
                  </span>
                </WdsText>
              </WdsStack>
              <WdsStack align-center gap-lg>
                <div v-for="bulk of activeBulkActions" :key="bulk.name">
                  <WdsTooltip :text="bulk.tooltip" :disabled="!bulk.tooltip">
                    <WdsButton
                      v-if="typeof bulk.action === 'function' && bulk.buttonVariant === 'solid'"
                      :primary="bulk.type !== 'secondary' && bulk.type !== 'error'"
                      :secondary="bulk.type === 'secondary'"
                      :error="bulk.type === 'error'"
                      :disabled="bulk.disabled"
                      :data-test="bulk.dataTest"
                      @click="handleBulkAction(bulk.action)"
                    >
                      <WdsStack align-center gap-sm>
                        <WdsIcon v-if="bulk.icon" :name="bulk.icon" :size="16" />
                        <div>{{ bulk.name }}</div>
                      </WdsStack>
                    </WdsButton>
                    <WdsButtonGhost
                      v-else-if="typeof bulk.action === 'function'"
                      :secondary="bulk.type === 'secondary'"
                      :error="bulk.type === 'error'"
                      :disabled="bulk.disabled"
                      :data-test="bulk.dataTest"
                      @click="handleBulkAction(bulk.action)"
                    >
                      <WdsStack align-center gap-sm>
                        <WdsIcon v-if="bulk.icon" :name="bulk.icon" :size="16" />
                        <div>{{ bulk.name }}</div>
                      </WdsStack>
                    </WdsButtonGhost>
                    <component :is="bulk.action" v-else :selected="effectiveSelectedRows" />
                  </WdsTooltip>
                </div>
              </WdsStack>
            </WdsStack>
          </ActionBar>
        </div>
      </WdsStack>
      <GraphqlDownloadTableModal
        v-model:show="showDownloadModal"
        :query="fullModifiedQuery"
        :variables="queryVariables"
        :data-description="tableDescription"
      />
      <WdsDrawer
        v-model:show="workerDetailsSidebarVisible"
        position="right"
        dismissible
        width="420px"
        padding="0"
      >
        <WorkerDetailPanel
          :viewer-account-id="workerDetailsProps.viewerAccountId"
          :trusted-contact-id="workerDetailsProps.trustedContactId"
          :worker-id="workerDetailsProps.workerId"
          :tab-key="workerDetailsProps.tabKey"
          @close="closeDetails"
          @updated="refetch"
          @deleted="refetch"
        />
      </WdsDrawer>
      <WdsDrawer
        v-model:show="recruiterDetailsSidebarVisible"
        position="right"
        dismissible
        width="420px"
        padding="0"
      >
        <RecruiterDetailPanel
          v-if="recruiterDetailsProps.companyRecruiterId"
          :company-recruiter-id="recruiterDetailsProps.companyRecruiterId"
          @close="closeRecruiterDetails"
          @updated="refetch"
          @deleted="refetch"
        />
      </WdsDrawer>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  WdsButton,
  WdsButtonGhost,
  WdsButtonIcon,
  WdsDrawer,
  WdsIcon,
  WdsStack,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { Kind, print } from 'graphql'
import {
  computed,
  ComputedRef,
  useId,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  reactive,
  ref,
  useSlots,
  useTemplateRef,
  watch,
} from 'vue'
import { useApolloClient, useQuery } from '@vue/apollo-composable'
import { useFullscreen } from '@vueuse/core'
import GraphqlDataTableFilters from '@/components/GraphqlDataTable/GraphqlDataTableFilters.vue'
import BaseDataTable from '@/components/BaseDataTable/BaseDataTable.vue'
import { convertToPrimitive, dotAccess, findProperty } from '@/utils/tools'
import type { GraphqlColumnMap } from '@/components/GraphqlDataTable/columns/types'
import WorkerDetailPanel from '@/components/WorkerDetailPanel/WorkerDetailPanel.vue'
import RecruiterDetailPanel from '@/components/RecruiterDetailPanel/RecruiterDetailPanel.vue'
import ActionBar from '@/components/ActionBar.vue'
import {
  addOpenWorkerDetailPanelListener,
  removeOpenWorkerDetailPanelListener,
  OpenWorkerDetailPanelInput,
  addOpenRecruiterDetailPanelListener,
  removeOpenRecruiterDetailPanelListener,
  OpenRecruiterDetailPanelInput,
} from '@/components/GraphqlDataTable/listners'
import {
  useQueryType,
  useModifiedQuery,
  useQueryColumns,
  useColumnOrdering,
  useExpandedColumns,
  useTableUrlState,
} from '@/components/GraphqlDataTable/composables'
import { tableGlobalsClass, tableScopeId } from '@/components/GraphqlDataTable/tools'
import { SortOrder } from '@/gql/graphql'
import { $t } from '@/composables/translation'
import BaseLoadingNotice from '@/components/BaseLoadingNotice.vue'
import GraphqlDataTableControlBar from '@/components/GraphqlDataTable/GraphqlDataTableControlBar.vue'
import { SortOption } from '@/components/BaseDataTable/composables/dataTableSort'
import useQueryEncoder from '@/composables/useQueryEncoder'
import GraphqlDownloadTableModal from '@/components/GraphqlDataTable/GraphqlDownloadTableModal.vue'
import { DataDescriber, SheetDescriber, TableDescriber } from '@/utils/apiDataExports'
import GraphqlDataTableErrorNotice from '@/components/GraphqlDataTable/GraphqlDataTableErrorNotice.vue'
import GraphqlDataTableQuickFilter from '@/components/GraphqlDataTable/filters/GraphqlDataTableQuickFilter.vue'
import {
  atobSafe,
  btoaSafe,
  countActiveFilters,
  resetFilters,
} from '@/components/GraphqlDataTable/filters/filter-tools'
import {
  TableFullscreen,
  TableGlobalsSelector,
  TableScope,
} from '@/components/GraphqlDataTable/injection'
import { BulkActionFunction, DataTableProps } from '@/components/GraphqlDataTable/types'
import { RowSelectionMode } from '@/components/GraphqlDataTable/enums'
import { Column } from '@/vue-prop-types/DataTable/types'

const slots = useSlots()

const emit = defineEmits<{
  fullScreen: [mode: boolean]
  rowClick: [row: any]
  'update:preSelectedCount': [count: number]
}>()

const table = useTemplateRef<HTMLElement>('table')

const { isFullscreen, enter, exit } = useFullscreen(table)
const toggleFullscreen = (state: boolean) => {
  if (!table.value) return
  emit('fullScreen', state)
  if (state) {
    enter()
  } else {
    exit()
  }
}

provide(TableFullscreen, isFullscreen)

const props = withDefaults(defineProps<DataTableProps>(), {
  clientId: 'publicApi',
  maxHeightAdjustment: '',
  filterBlackList: undefined,
  columnBlackList: undefined,
  quickFilters: undefined,
  bulkActions: () => [],
  dataTest: 'graphql-data-table',
  enabledRowSelectionModes: () => [RowSelectionMode.PAGE],
  allowCrossTableColumns: false,
  multiSelect: false,
  initialSelectedIds: () => [],
  containBulkActionBar: false,
  defaultPerPage: 50,
  defaultPerPageOptions: () => [10, 50, 100],
  preserveSelectedRows: false,
  preSelectedCount: 0,
})

const { persistedKey } = useTableUrlState(props.query)

const { clientId } = props

const stringifiedQuery: ComputedRef<string> = computed(() => print(props.query))

// Each table's overlays teleport into its own wrapper, not the first one in the document.
const tableInstanceId = useId()
const tableScope = tableScopeId(props.query, tableInstanceId)
const globalsClass = tableGlobalsClass(props.query, tableInstanceId)

provide(TableGlobalsSelector, `.${globalsClass}`)
// Detail panels are rendered by this table, so only rows inside it should be able to open one.
provide(TableScope, tableScope)

const { args: queryArgs } = useQueryType(stringifiedQuery)

const maxHeightAdjust = computed(() => {
  if (!props.maxHeightAdjustment) return ''
  if (typeof props.maxHeightAdjustment === 'number') return `- ${props.maxHeightAdjustment}px`
  return `- ${props.maxHeightAdjustment}`
})

const searchArg = computed(() =>
  queryArgs.value.inputtedArgs.filter((arg) => !!arg).find(({ name }) => name === 'search'),
)
const paginationPage = useQueryEncoder(persistedKey('page'), 1, (raw: string) =>
  raw === '' ? 1 : convertToPrimitive(raw, Number as any),
)
const paginationPerPage = useQueryEncoder(
  persistedKey('per-page'),
  props.defaultPerPage,
  (raw: string) => (raw === '' ? props.defaultPerPage : convertToPrimitive(raw, Number as any)),
)

const search = useQueryEncoder(persistedKey('search'), '')

const ob = defineModel<{ field: string; order: SortOrder | null } | undefined>('orderBy')
const orderBy = useQueryEncoder(
  persistedKey('orderBy'),
  ob,
  (raw: string) => {
    if (raw === '') return ob.value
    return JSON.parse(atobSafe(raw))
  },
  (data: object) => btoaSafe(JSON.stringify(data)),
)

const defaultBlacklist = ['search', 'orderBy']

const filterBlackList = computed(() => [
  ...new Set([
    ...(props.filterBlackList || []),
    ...defaultBlacklist,
    // Prevent additionalQueryVariables from being automatically added as filters
    ...Object.keys(props.additionalQueryVariables || {}),
  ]),
])

const afv = defineModel<Record<string, any>>('filters', {
  default: () => ({}),
})

const activeFilterValues = ref({})

onMounted(() => {
  activeFilterValues.value = { ...afv.value }
})

watch(
  [search, activeFilterValues],
  () => {
    paginationPage.value = 1 // Any time search or filters changes we reset to first page
  },
  { deep: true },
)

const queryVariables = computed(() => {
  const activeFilters = activeFilterValues.value ?? {}
  const filters: Record<string, any> = Object.keys(activeFilters)
    .map((key) => [
      key,
      activeFilters[key]?.map?.((filter) => filter?.value ?? filter) ?? activeFilters[key],
    ])
    .filter(
      // Filter out empty arrays and falsy values
      ([_, value]) =>
        (Array.isArray(value) && value.length > 0) ||
        (!Array.isArray(value) && !!value) ||
        value === false,
      // For boolean values we might need  to take into consideration if the values are required
      // || value === false, // We are okay with the pure false value.
    )
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})

  if (searchArg.value) {
    filters.search = search.value
  }

  return {
    // Default to always have the current account but can be overwritten by filters
    accounts: [currentAccount.globalId],
    ...filters,
    page: paginationPage.value,
    first: paginationPerPage.value,
    orderBy: orderBy.value ? [orderBy.value] : [],
    // Include additional variables if provided
    ...(props.additionalQueryVariables || {}),
  }
})

const activeFilterValueCounts = computed(() =>
  countActiveFilters(Object.values(activeFilterValues.value ?? {})),
)

const loadDataRegardlessOfError = ref(false)

watch(queryVariables, () => {
  loadDataRegardlessOfError.value = false
})

const { columnsWhiteList, columnOrdering, expandedColumnTitles, dragEventHandler } =
  useColumnOrdering(stringifiedQuery)

const {
  modifiedQuery,
  fullModifiedQuery,
  paginatedQuery,
  paginationQueryReady,
  modifiedQueryReady,
} = useModifiedQuery(stringifiedQuery, columnsWhiteList)

// Skip the fallback pq: running the page's own query wastes a request and leaves stale rows.
const { result: paginationResult } = useQuery(
  paginatedQuery,
  () => queryVariables.value,
  () => ({
    clientId,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    enabled: paginationQueryReady.value,
  }),
)

// While pq is the fallback, its row data may nest partial paginatorInfo the deep search must skip.
const paginatorInfo = computed(() =>
  paginationQueryReady.value ? findProperty('paginatorInfo', paginationResult.value) : undefined,
)

const showFilters = ref(false)

const OperationDefinition = 'OperationDefinition'
const BlackListFragments = computed(() => [
  'PaginatorInfoFragment',
  ...(props.columnBlackList || []),
])

const { columns: columnDefinitions } = useQueryColumns(modifiedQuery)

const {
  result: queryResult,
  error,
  loading: queryLoading,
  refetch,
} = useQuery(
  () => modifiedQuery.value,
  () => queryVariables.value,
  () => ({
    clientId,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    // Wait for introspection: the query it produces replaces the page's own, which would
    // otherwise be sent first and then again.
    enabled: modifiedQueryReady.value,
  }),
)

const expandedColumns = useExpandedColumns(columnsWhiteList, expandedColumnTitles)

const columns = computed(() => {
  if (!columnDefinitions.value || columnDefinitions.value.length === 0) return []
  const orderIndex = new Map(columnOrdering.value.map((k, i) => [k, i] as [string, number]))
  const dataColumns: Column[] = columnDefinitions.value
    .filter(
      ({ name, columnDescriber }) =>
        !BlackListFragments.value.includes(name) && !!columnDescriber?.columnDef,
    )
    .filter(({ name }) => columnsWhiteList.value[name])
    .map(({ columnDescriber }) => columnDescriber)
    .map((columnDescriber) => {
      if (
        columnDescriber.sort !== undefined &&
        columnDescriber.sort?.length > 0 &&
        columnDescriber.columnDef.orderAble
      ) {
        const sortingMatch = columnDescriber.sort.find(
          ({ orderByColumn, sortOrder }) =>
            orderByColumn === orderBy.value?.field && sortOrder === orderBy.value?.order,
        )
        if (!!sortingMatch && sortingMatch.sortOrder) {
          // eslint-disable-next-line no-param-reassign
          columnDescriber.columnDef.order = sortingMatch.sortOrder.toLowerCase()
        } else {
          // Since our column is not the currently ordered one, we remove the order from it
          // Otherwise multiple columns will look like they are in some order.
          // eslint-disable-next-line no-param-reassign
          columnDescriber.columnDef.order = undefined
        }
      }

      return columnDescriber.columnDef
    })
    .map((column: Column) => ({ ...column, draggable: true }))

  // Merge expanded columns
  for (const { columnDescriber } of expandedColumns.value) {
    dataColumns.push({ ...columnDescriber.columnDef, draggable: true })
  }

  // Sort all columns (including expanded) by ordering
  dataColumns.sort((a: Column, b: Column) => {
    if (columnOrdering.value.length === 0) return 0
    const ai = orderIndex.has(a.key) ? (orderIndex.get(a.key) ?? 0) : Infinity
    const bi = orderIndex.has(b.key) ? (orderIndex.get(b.key) ?? 0) : Infinity
    return ai - bi
  })
  // Adding action column if it exists
  if (slots.action) {
    dataColumns.push({
      title: '',
      key: 'action-column',
      slot: 'action',
      ignoreClickForRowSelection: true,
      sticky: true,
    })
  }
  return dataColumns
})

// We have to handle the case where the underlying result data returns before our column
// Introspection data has managed to be setup.
const loading = computed(
  () => !modifiedQueryReady.value || queryLoading.value || columns.value.length === 0,
)
const result = computed(() => (columns.value.length > 0 ? queryResult.value : null))

const handleTableSearch = ({ page, perPage, order } = { page: 1, perPage: 50, order: {} }) => {
  paginationPage.value = page + 1 // BaseDataTable page numbers are 0 indexed but Gql are 1 indexed
  paginationPerPage.value = perPage
  const orderSafe = order || {}

  const cols = columnDefinitions.value
    .map(({ columnDescriber }) => columnDescriber)
    .filter((column) => !!orderSafe[column?.columnDef?.key])

  for (const col of cols) {
    // So if we have a custom sort function, we will use that to update our ordering
    if (col.sort && col.sort.length > 0) {
      const sortOption: SortOption = col.sort[0]
      orderBy.value = {
        field: sortOption.orderByColumn,
        order: orderSafe[col.columnDef.key]?.toUpperCase(),
      }
    }
  }
}

// Our array of complex query data
const entries: ComputedRef<object[]> = computed(() => {
  // Get the operation definition
  const def = props.query.definitions.find(({ kind }) => kind === OperationDefinition)
  const selectionPath = def?.selectionSet.selections?.[0] // We assume only one query path has been made
  if (selectionPath?.kind !== Kind.INLINE_FRAGMENT) {
    const path = `${selectionPath?.name?.value}.data`
    return dotAccess(result.value, path)
  }
  return result.value
})

// ===================== Bulk actions handling ======================
const selectionMode = defineModel<RowSelectionMode>('selectionMode', {
  default: RowSelectionMode.PAGE,
})
const isUsingSelectionModePage = computed(() => selectionMode.value === RowSelectionMode.PAGE)
const isUsingSelectionModeAll = computed(() => selectionMode.value === RowSelectionMode.ALL)
const selectionModeContext = reactive({
  all: {
    excludeIds: [] as (string | number)[],
  },
})

const totalRowCount = computed(() => paginatorInfo.value?.total ?? 0)

const totalCountModel = defineModel<number>('totalCount', { default: 0 })
watch(
  totalRowCount,
  (count) => {
    totalCountModel.value = count
  },
  { immediate: true },
)

const toggleExclusion = (id: string | number, excluded: boolean) => {
  if (excluded) {
    if (!selectionModeContext.all.excludeIds.includes(id)) {
      selectionModeContext.all.excludeIds.push(id)
    }
  } else {
    selectionModeContext.all.excludeIds = selectionModeContext.all.excludeIds.filter(
      (i) => i !== id,
    )
  }
}

const resetFullScopeSelection = () => {
  selectionModeContext.all.excludeIds = []
}

const resetAllFilters = () => {
  resetFilters(activeFilterValues)
}

// Support both page-only and all-rows selection modes.
// page-only mode uses real individual row selections.
// all-rows mode uses 'all except excluded' selections.
const selectedRowsForPage = ref<Record<string, object>>({})

const preSelectionProcessed = ref(new Set<string | number>())

const loadedPreSelectedCount = ref(0)
// Clearing the selection drops the pre-selection too, including rows not loaded yet.
const preSelectionCleared = ref(false)
watch(entries, async (newEntries) => {
  if (!props.isRowPreSelected || preSelectionCleared.value || !newEntries?.length) return

  // BaseDataTable clears selections when entries change.
  // Wait for that to settle before applying pre-selections.
  await nextTick()

  let changed = false
  const newSelections = { ...selectedRowsForPage.value }

  for (const entry of newEntries) {
    const { id } = entry as Record<string, any>
    if (id != null && !preSelectionProcessed.value.has(id)) {
      preSelectionProcessed.value.add(id)
      if (props.isRowPreSelected(entry)) {
        loadedPreSelectedCount.value += 1
        newSelections[id] = entry
        changed = true
      }
    }
  }

  if (changed) {
    selectedRowsForPage.value = newSelections
  }
})
const effectiveSelectedRows = computed<Record<string, object>>({
  get: () => {
    if (isUsingSelectionModePage.value) {
      return selectedRowsForPage.value
    }

    return (entries.value || []).reduce(
      (acc: Record<string, object>, entry): Record<string, object> => {
        if (
          Object.hasOwn(entry, 'id') &&
          !selectionModeContext.all.excludeIds.includes((entry as { id: string }).id)
        ) {
          acc[(entry as { id: string }).id] = entry
        }

        return acc
      },
      {},
    )
  },
  set: (newValue: Record<string, object>) => {
    if (isUsingSelectionModePage.value) {
      selectedRowsForPage.value = newValue
    }
  },
})

const selectedRowsModel = defineModel<Record<string, object>>('selectedRows')

let hasAppliedInitialSelection = false
watch(
  () => [entries.value, props.initialSelectedIds] as const,
  ([entriesVal, initialIds]) => {
    if (!props.multiSelect || !initialIds?.length || !entriesVal?.length) return
    if (hasAppliedInitialSelection) return
    hasAppliedInitialSelection = true
    const idSet = new Set(initialIds)
    const next: Record<string, object> = {}
    for (const entry of entriesVal) {
      if (Object.hasOwn(entry, 'id') && idSet.has((entry as { id: string }).id)) {
        next[(entry as { id: string }).id] = entry
      }
    }
    selectedRowsForPage.value = next
  },
  { immediate: true },
)

// Sync selection to parent when using multiSelect and v-model:selectedRows.
watch(
  effectiveSelectedRows,
  (val) => {
    if (selectedRowsModel.value !== undefined) {
      selectedRowsModel.value = { ...val }
    }
  },
  { deep: true },
)

const selectedRowCount = computed(() => {
  if (isUsingSelectionModeAll.value) {
    return totalRowCount.value - selectionModeContext.all.excludeIds.length
  }

  const loadedSelected = Object.keys(effectiveSelectedRows.value).length

  const unloadedPreSelected = preSelectionCleared.value
    ? 0
    : Math.max(0, props.preSelectedCount - loadedPreSelectedCount.value)
  return loadedSelected + unloadedPreSelected
})

const clearSelection = () => {
  if (isUsingSelectionModeAll.value) {
    resetFullScopeSelection()
    selectionMode.value = RowSelectionMode.PAGE
  } else {
    selectedRowsForPage.value = {}
  }
  preSelectionCleared.value = true
  emit('update:preSelectedCount', 0)
}

// Handed to the action slot: buttons there sit outside row selection but should toggle it like a row click.
const toggleRowSelection = (row: { id: string | number }) => {
  const selected = row.id in effectiveSelectedRows.value

  if (isUsingSelectionModeAll.value) {
    toggleExclusion(row.id, selected)
    return
  }

  const next = { ...selectedRowsForPage.value }

  if (selected) {
    delete next[row.id]
  } else {
    next[row.id] = row
  }

  selectedRowsForPage.value = next
}

const { resolveClient } = useApolloClient()
const selectingAllRows = ref(false)

// Selects every row matching the current search/filters, not just the loaded page.
const selectAllRows = async (): Promise<void> => {
  if (totalRowCount.value === 0 || selectingAllRows.value) return

  selectingAllRows.value = true
  const requestedVariables = JSON.stringify(queryVariables.value)
  try {
    const { data } = await resolveClient(clientId).query({
      query: fullModifiedQuery.value,
      variables: { ...queryVariables.value, page: 1, first: totalRowCount.value },
      fetchPolicy: 'network-only',
    })

    // Search or filters moved on while loading: those rows no longer belong to this view.
    if (JSON.stringify(queryVariables.value) !== requestedVariables) return

    const def = props.query.definitions.find(({ kind }) => kind === OperationDefinition)
    const selectionPath = def?.selectionSet.selections?.[0]
    if (selectionPath?.kind === Kind.INLINE_FRAGMENT || !selectionPath?.name?.value) return

    const rows: Array<{ id?: string | number }> =
      dotAccess(data, `${selectionPath.name.value}.data`) ?? []
    // Pre-selected rows from unloaded pages now count as loaded, or the total counts them twice.
    rows
      .filter((row) => row?.id != null && !preSelectionProcessed.value.has(row.id))
      .forEach((row) => {
        preSelectionProcessed.value.add(row.id as string | number)
        if (props.isRowPreSelected?.(row)) loadedPreSelectedCount.value += 1
      })
    // Added to the selection: rows picked outside the current filters stay picked.
    selectedRowsForPage.value = {
      ...selectedRowsForPage.value,
      ...Object.fromEntries(
        rows.filter((row) => row?.id != null).map((row) => [row.id as string | number, row]),
      ),
    }
  } finally {
    selectingAllRows.value = false
  }
}

// Parents ask for select-all through this model; it flips back to false once every row is selected.
const selectAllModel = defineModel<boolean>('selectAll', { default: false })

watch(selectAllModel, async (requested) => {
  if (!requested) return
  try {
    await selectAllRows()
  } finally {
    selectAllModel.value = false
  }
})

const showDownloadModal = ref(false)

// Used for data exports
const tableDescription: ComputedRef<TableDescriber<any>> = computed(() => {
  const dataDescriptions: DataDescriber<any>[] = columnDefinitions.value
    .filter(({ name }) => columnsWhiteList.value[name])
    .map(({ columnDescriber }) => columnDescriber) // We need to add the mapping here
    .flatMap((columDescription: GraphqlColumnMap) => {
      if (columDescription?.path) {
        // Here we unwrap the underlying function so it gets access to the data in the format
        // it is designed for
        return columDescription.dataDescriber.map((describer) => ({
          title: describer.title,
          extraction: (row: object) =>
            describer.extraction(dotAccess(row, columDescription.path) ?? {}),
        }))
      }
      return columDescription?.dataDescriber
    })
    .filter((des) => des)

  const sheetDescription: SheetDescriber<any> = {
    columns: dataDescriptions,
    title: 'Sheet',
  }

  return {
    title: '',
    sheets: [sheetDescription],
  }
})

const workerDetailsSidebarVisible = ref(false)
const workerDetailsProps = reactive({
  workerId: '',
  trustedContactId: '',
  viewerAccountId: currentAccount.globalId,
  tabKey: '',
})

const closeDetails = () => {
  workerDetailsProps.workerId = ''
  workerDetailsProps.trustedContactId = ''
  workerDetailsProps.viewerAccountId = currentAccount.globalId
  workerDetailsProps.tabKey = ''
  workerDetailsSidebarVisible.value = false
}

const openWorkerDetailPanel = (input: OpenWorkerDetailPanelInput) => {
  workerDetailsProps.workerId = input.workerAccountId
  if (input.viewerAccountId) {
    workerDetailsProps.viewerAccountId = input.viewerAccountId
  }
  workerDetailsProps.tabKey = input.tabKey ?? ''
  workerDetailsSidebarVisible.value = true
}

addOpenWorkerDetailPanelListener(openWorkerDetailPanel, tableScope)

const recruiterDetailsSidebarVisible = ref(false)
const recruiterDetailsProps = reactive({
  companyRecruiterId: '',
})

const closeRecruiterDetails = () => {
  recruiterDetailsProps.companyRecruiterId = ''
  recruiterDetailsSidebarVisible.value = false
}

const handleOpenRecruiterDetailPanel = (input: OpenRecruiterDetailPanelInput) => {
  recruiterDetailsProps.companyRecruiterId = input.companyRecruiterId
  recruiterDetailsSidebarVisible.value = true
}

addOpenRecruiterDetailPanelListener(handleOpenRecruiterDetailPanel, tableScope)

// Without this a closed table keeps answering clicks, opening a panel that nothing renders.
onUnmounted(() => {
  removeOpenWorkerDetailPanelListener(openWorkerDetailPanel)
  removeOpenRecruiterDetailPanelListener(handleOpenRecruiterDetailPanel)
})

const bulkActionsActive = computed(() => props.bulkActions.length > 0)
const hasPersistentBulkAction = computed(() => props.bulkActions.some((a) => a.persistent))
const activeBulkActions = computed(() => props.bulkActions.filter((bulk) => !bulk.hidden))

const handleBulkAction = (action: BulkActionFunction) => {
  if (isUsingSelectionModeAll.value) {
    action(
      effectiveSelectedRows.value,
      'full-scope',
      selectionModeContext.all.excludeIds,
      totalRowCount.value,
    )
    return
  }
  action(effectiveSelectedRows.value)
}

const handleRowSelectionToggled = (row: any, selected: boolean) => {
  if (isUsingSelectionModeAll.value) {
    toggleExclusion(row.id, !selected)
  }
}

const handleSelectAll = () => {
  if (isUsingSelectionModeAll.value) {
    resetFullScopeSelection()
    selectionMode.value = RowSelectionMode.ALL
  }
}

const handleDeselectAll = () => {
  if (isUsingSelectionModeAll.value) {
    resetFullScopeSelection()
    selectionMode.value = RowSelectionMode.PAGE
  }
}
</script>

<style lang="scss" scoped>
.graphql-table-wrapper {
  padding-top: 20px;
  background: white;
  .field-table {
    margin-top: 3rem;
    &:deep(.table-wrap) {
      max-height: 300px;
      overflow: auto;
    }
  }

  &.isFullscreen {
    padding-top: 0;
  }

  &--contain-bulk-bar {
    position: relative;
  }

  .query-table {
    margin-top: 3rem;
    &:deep(.table-wrap) {
      overflow: auto;
    }
  }

  // Slight improvement to the pagination menu, but more improvements are needed on the component itself
  &:deep(.wds-pagination-dropdown) {
    z-index: 9999999; // We want to be on top of the sticky action menu
    max-height: 10rem !important;
    li {
      padding: 0 0.2rem;
    }
  }
}
.v-split {
  border-left: 1px solid #c1c1cb;
  height: 16px;
}
</style>
